'use client'

import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useState,
  useEffect
} from 'react'
import { useRouter } from 'next/router'
import { Fit } from 'types/fit'
import { FilterOption } from 'types/filters'
import { ADDITIONAL_PROPERTIES, COOKIE_EXPIRE_1_YEAR } from '@consts/index'
import { getFiltersOptionsState } from '@helpers/filters'
import { initFit } from '@helpers/initFit'
import { setStoredValue, getStorageValue } from './useStorage'
import { getCustomerSavedFitFromCookies } from '@helpers/customer/fit'
import { setCookie } from '@helpers/session'

interface FitContextType {
  filters: FilterOption[]
  savedFit: Fit | null
  isSavedFitApplied: boolean
  fitUpdatedAt?: string
  storeFilters: (filterOptions: FilterOption[]) => void
  updateFilters: (filterOptions: FilterOption[]) => void
  applySavedFit: (
    fit: Fit,
    updatedAt?: string,
    shouldApplyFilters?: boolean
  ) => void
  removeSavedFit: () => void
  storeSavedFitApplied: (applied: boolean) => void
  getHasCustomerSavedFit: () => boolean
}

const FiltersContext = createContext<FitContextType | undefined>(undefined)

interface FitProviderProps {
  children: ReactNode
}

export const FIT_STORAGE_KEYS = {
  FILTERS: 'filters',
  SAVED_FIT: 'saved_fit',
  SAVED_FIT_APPLIED: 'plp_filters_saved_fit_applied',
  LAST_COLLECTION_PAGE: 'last_collection_page'
} as const

function createFilterStorage(filterOptions: FilterOption[]) {
  return filterOptions.reduce((filters, o) => {
    return (
      ADDITIONAL_PROPERTIES[o.type as keyof typeof ADDITIONAL_PROPERTIES] || {}
    ).skipInCookies
      ? filters
      : {
          ...filters,
          [o.type === 'shortsleg' ? 'shortsLeg' : o.type]: /waist|leg/.test(
            o.type
          )
            ? parseInt(o.value, 10)
            : o.value.toString()
        }
  }, {})
}

function getSavedFitAppliedFromLocalStorage(): boolean {
  const savedFitAppliedState = getStorageValue(
    'sessionStorage',
    FIT_STORAGE_KEYS.SAVED_FIT_APPLIED,
    null
  )
  return savedFitAppliedState || false
}

function getInitialFit(): FilterOption[] {
  if (typeof window === 'undefined') return []

  const storedFilters = initFit()
  return getFiltersOptionsState(storedFilters)
}

function getCollectionPageFromPath(
  pathname: string,
  collectionHandle: string
): string | null {
  if (pathname.startsWith('/collections/')) {
    return collectionHandle || null
  }
  return null
}

export function FitProvider({ children }: FitProviderProps) {
  const [filters, setFilters] = useState<FilterOption[]>(getInitialFit())
  const [savedFit, setSavedFit] = useState<Fit | null>(
    getCustomerSavedFitFromCookies()
  )
  const [fitUpdatedAt, setFitUpdatedAt] = useState<string | undefined>(
    undefined
  )
  const [isSavedFitApplied, setSavedFitApplied] = useState<boolean>(
    getSavedFitAppliedFromLocalStorage()
  )
  const router = useRouter()

  const storeFilters = useCallback((filterOptions: FilterOption[]) => {
    setFilters(filterOptions)
    const sessionStorageFilters = createFilterStorage(filterOptions)
    setStoredValue(
      'sessionStorage',
      FIT_STORAGE_KEYS.FILTERS,
      sessionStorageFilters
    )
  }, [])

  const updateFilters = useCallback((filterOptions: FilterOption[]) => {
    // If changing filters when the saved fit isn't applied, forget the last collection so that it resets filters
    // on next collection page visit
    if (!isSavedFitApplied) {
      setStoredValue(
        'sessionStorage',
        FIT_STORAGE_KEYS.LAST_COLLECTION_PAGE,
        ''
      )
    }

    setFilters(prev => {
      const next = [...prev]

      filterOptions.forEach(filter => {
        const index = next.findIndex(f => f.type === filter.type)
        if (index === -1) next.push(filter)
        else if (
          next[index].value?.toLowerCase() === filter.value?.toLowerCase()
        )
          next.splice(index, 1)
        else next[index] = { ...filter }
      })

      return next
    })
  }, [])

  const applySavedFit = useCallback(
    (fit: Fit, updatedAt?: string, shouldApplyFilters?: boolean) => {
      setSavedFit(fit)

      if (updatedAt) {
        setFitUpdatedAt(updatedAt)
      }

      // Store in cookie
      const fitAsString = JSON.stringify(fit)
      setCookie(
        FIT_STORAGE_KEYS.SAVED_FIT,
        fitAsString,
        COOKIE_EXPIRE_1_YEAR,
        true
      )

      // If Saved Fit applied then update filters
      const applyFilters = shouldApplyFilters ?? isSavedFitApplied
      if (applyFilters) {
        storeFilters(getFiltersOptionsState(fit))
      }
    },
    [isSavedFitApplied, storeFilters]
  )

  const getHasCustomerSavedFit = useCallback(() => {
    return !!savedFit && Object.keys(savedFit || {}).length > 0
  }, [savedFit])

  const removeSavedFit = useCallback(() => {
    setSavedFit(null)
    setSavedFitApplied(false)

    // Remove cookie
    document.cookie = `${FIT_STORAGE_KEYS.SAVED_FIT}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`

    // Remove from sessionStorage
    sessionStorage.removeItem(FIT_STORAGE_KEYS.SAVED_FIT_APPLIED)
  }, [])

  const storeSavedFitApplied = useCallback((applied: boolean) => {
    setStoredValue(
      'sessionStorage',
      FIT_STORAGE_KEYS.SAVED_FIT_APPLIED,
      applied
    )
    setSavedFitApplied(applied)
  }, [])

  // Detect page changes and reset filters if navigating to a different collection
  useEffect(() => {
    if (!router.isReady) return

    const currentCollection = getCollectionPageFromPath(
      router.pathname,
      router.query?.collectionHandle as string
    )
    const lastCollection = getStorageValue<string | null>(
      'sessionStorage',
      FIT_STORAGE_KEYS.LAST_COLLECTION_PAGE,
      null
    )

    // If we're on a collection page and the saved fit isn't applied, check if we navigated to a different collection than last time to reset filters
    if (!isSavedFitApplied && currentCollection) {
      if (lastCollection !== null && lastCollection !== currentCollection) {
        storeFilters([])
      }
      setStoredValue(
        'sessionStorage',
        FIT_STORAGE_KEYS.LAST_COLLECTION_PAGE,
        currentCollection
      )
    }
  }, [
    router.isReady,
    router.pathname,
    router.query?.collectionHandle,
    isSavedFitApplied,
    storeFilters
  ])

  return (
    <FiltersContext.Provider
      value={{
        filters,
        savedFit,
        isSavedFitApplied,
        fitUpdatedAt,
        storeFilters,
        updateFilters,
        applySavedFit,
        removeSavedFit,
        storeSavedFitApplied,
        getHasCustomerSavedFit
      }}
    >
      {children}
    </FiltersContext.Provider>
  )
}

export function useFit(): FitContextType {
  const context = useContext(FiltersContext)
  if (context === undefined) {
    throw new Error('useFit must be used within a FitProvider')
  }
  return context
}
